import {
  createSeamlessAudioLoop,
  inspectAudioLoopEdges,
} from '../../src/game/audioLoop.js';

const WAVES=new Set(['sine','triangle','sawtooth','square']);
const FILTERS=new Set(['lowpass','bandpass','highpass']);
let context=null;
let noiseBuffer=null;
let activeEngineSamplePreview=null;
let activeEngineSampleLoop=null;
let activeOneShotSamplePreview=null;
const decodedAudioSamples=new WeakMap();
const MAX_ENGINE_SAMPLE_BYTES=50*1024*1024;

const number=(value,fallback,min,max)=>{
  const parsed=Number(value);
  return Number.isFinite(parsed)?Math.max(min,Math.min(max,parsed)):fallback;
};

function ensureAudio(){
  const AudioContext=window.AudioContext||window.webkitAudioContext;
  if(!AudioContext)return null;
  context||=new AudioContext();
  if(context.state==='suspended')context.resume();
  if(!noiseBuffer){
    noiseBuffer=context.createBuffer(1,Math.ceil(context.sampleRate*2),context.sampleRate);
    const data=noiseBuffer.getChannelData(0);
    for(let index=0;index<data.length;index+=1)data[index]=Math.random()*2-1;
  }
  return context;
}

function isMp3File(file){
  const type=String(file?.type||'').toLowerCase();
  return /\.mp3$/i.test(file?.name||'')||type==='audio/mpeg'||type==='audio/mp3';
}

async function decodeAudioSample(file){
  if(!file)throw new Error('Elegí un archivo MP3.');
  if(!isMp3File(file))throw new Error('El archivo debe ser un MP3.');
  if(!file.size)throw new Error('El archivo MP3 está vacío.');
  if(file.size>MAX_ENGINE_SAMPLE_BYTES)throw new Error('El MP3 supera el límite de 50 MB.');
  const audio=ensureAudio();
  if(!audio)throw new Error('Este navegador no permite previsualizar audio.');
  let decoded=decodedAudioSamples.get(file);
  if(!decoded){
    decoded=audio.decodeAudioData(await file.arrayBuffer()).catch(()=>{
      throw new Error('No se pudo decodificar el MP3. Revisá que el archivo no esté dañado.');
    });
    decodedAudioSamples.set(file,decoded);
  }
  return decoded;
}

export async function inspectEngineSample(file){
  const buffer=await decodeAudioSample(file);
  if(buffer.duration<.5)throw new Error('El loop debe durar al menos 0,5 segundos.');
  const edges=inspectAudioLoopEdges(buffer);
  const warnings=[];
  if(buffer.duration<2)warnings.push('Es muy corto; un loop de 2 a 10 segundos suele repetir mejor.');
  if(buffer.duration>12)warnings.push('Es largo; recortarlo reduce memoria y facilita un loop parejo.');
  return {
    kind:'engine-idle-loop',
    fileName:file.name,
    mediaType:file.type||'audio/mpeg',
    byteSize:file.size,
    duration:Number(buffer.duration.toFixed(3)),
    sampleRate:buffer.sampleRate,
    channels:buffer.numberOfChannels,
    ...edges,
    warnings,
  };
}

export async function inspectOneShotSample(file){
  const buffer=await decodeAudioSample(file);
  if(buffer.duration>.001&&buffer.duration<.03){
    throw new Error('El disparo es demasiado corto para reproducirse correctamente.');
  }
  if(buffer.duration>15){
    throw new Error('El disparo no puede durar más de 15 segundos.');
  }
  const warnings=[];
  if(buffer.duration>5)warnings.push('Es un disparo largo; conviene recortarlo a menos de 5 segundos.');
  return {
    kind:'cannon-shot',
    fileName:file.name,
    mediaType:file.type||'audio/mpeg',
    byteSize:file.size,
    duration:Number(buffer.duration.toFixed(3)),
    sampleRate:buffer.sampleRate,
    channels:buffer.numberOfChannels,
    warnings,
  };
}

export async function inspectSustainedSample(file){
  const buffer=await decodeAudioSample(file);
  if(buffer.duration<.25)throw new Error('El loop debe durar al menos 0,25 segundos.');
  if(buffer.duration>15)throw new Error('El loop no puede durar más de 15 segundos.');
  const edges=inspectAudioLoopEdges(buffer);
  const warnings=[];
  if(buffer.duration<1)warnings.push('Es muy corto; un loop de 1 a 5 segundos suele repetir mejor.');
  if(buffer.duration>8)warnings.push('Es largo; recortarlo reduce memoria y facilita una repetición pareja.');
  return {
    kind:'nitro-loop',
    fileName:file.name,
    mediaType:file.type||'audio/mpeg',
    byteSize:file.size,
    duration:Number(buffer.duration.toFixed(3)),
    sampleRate:buffer.sampleRate,
    channels:buffer.numberOfChannels,
    ...edges,
    warnings,
  };
}

function stopEngineSamplePreview(){
  if(!activeEngineSamplePreview)return;
  try{activeEngineSamplePreview.stop();}
  catch{
    // La fuente puede haber terminado por sí sola.
  }
  activeEngineSamplePreview=null;
}

function stopActiveEngineSampleLoop(){
  activeEngineSampleLoop?.stop();
  activeEngineSampleLoop=null;
}

export async function startEngineSampleLoop(file,design={}){
  const audio=ensureAudio();
  if(!audio)throw new Error('Este navegador no permite previsualizar audio.');
  const decodedBuffer=await decodeAudioSample(file);
  const buffer=createSeamlessAudioLoop(audio,decodedBuffer,design);
  stopEngineSamplePreview();
  stopActiveEngineSampleLoop();
  const source=audio.createBufferSource();
  const filter=audio.createBiquadFilter();
  const gain=audio.createGain();
  let stopped=false;
  source.buffer=buffer;
  source.loop=true;
  filter.type='lowpass';
  gain.gain.value=.001;
  source.connect(filter);filter.connect(gain);gain.connect(audio.destination);
  const controller={
    update(nextDesign={}){
      if(stopped)return;
      const now=audio.currentTime;
      source.playbackRate.setTargetAtTime(
        number(nextDesign.sampleIdleRate,1,.5,1.5),
        now,
        .04,
      );
      filter.frequency.setTargetAtTime(
        number(nextDesign.sampleFilter,12000,500,16000),
        now,
        .05,
      );
      gain.gain.setTargetAtTime(
        number(nextDesign.sampleVolume,.28,.02,.8),
        now,
        .04,
      );
    },
    stop(){
      if(stopped)return;
      stopped=true;
      const now=audio.currentTime;
      gain.gain.setTargetAtTime(.001,now,.015);
      try{source.stop(now+.06);}
      catch{
        // La fuente puede haber terminado antes de cerrar la escucha.
      }
      if(activeEngineSampleLoop===controller)activeEngineSampleLoop=null;
    },
  };
  source.addEventListener('ended',()=>{
    if(activeEngineSampleLoop===controller)activeEngineSampleLoop=null;
    source.disconnect();
    filter.disconnect();
    gain.disconnect();
  },{once:true});
  activeEngineSampleLoop=controller;
  controller.update(design);
  source.start();
  return controller;
}

export async function previewEngineSample(file,design={}){
  const audio=ensureAudio();
  if(!audio)throw new Error('Este navegador no permite previsualizar audio.');
  const decodedBuffer=await decodeAudioSample(file);
  const buffer=createSeamlessAudioLoop(audio,decodedBuffer,design);
  stopEngineSamplePreview();
  stopActiveEngineSampleLoop();
  const now=audio.currentTime;
  const duration=2.2;
  const source=audio.createBufferSource();
  const filter=audio.createBiquadFilter();
  const gain=audio.createGain();
  const idleRate=number(design.sampleIdleRate,1,.5,1.5);
  const speedRate=number(design.sampleSpeedRate,.04,0,.12);
  const boostRate=number(design.sampleBoostRate,.35,0,1.2);
  const volume=number(design.sampleVolume,.28,.02,.8);
  source.buffer=buffer;
  source.loop=true;
  source.playbackRate.setValueAtTime(idleRate,now);
  source.playbackRate.linearRampToValueAtTime(
    Math.min(3,idleRate+speedRate*14),
    now+1.35,
  );
  source.playbackRate.linearRampToValueAtTime(
    Math.min(3,idleRate+speedRate*17+boostRate),
    now+1.72,
  );
  source.playbackRate.linearRampToValueAtTime(idleRate,now+duration);
  filter.type='lowpass';
  filter.frequency.value=number(design.sampleFilter,12000,500,16000);
  gain.gain.setValueAtTime(.001,now);
  gain.gain.exponentialRampToValueAtTime(volume,now+.06);
  gain.gain.setValueAtTime(volume,now+duration-.12);
  gain.gain.exponentialRampToValueAtTime(.001,now+duration);
  source.connect(filter);filter.connect(gain);gain.connect(audio.destination);
  source.addEventListener('ended',()=>{
    if(activeEngineSamplePreview===source)activeEngineSamplePreview=null;
    source.disconnect();
    filter.disconnect();
    gain.disconnect();
  },{once:true});
  activeEngineSamplePreview=source;
  source.start(now);
  source.stop(now+duration+.03);
}

export async function previewOneShotSample(file,design={},prefix='sound'){
  const audio=ensureAudio();
  if(!audio)throw new Error('Este navegador no permite previsualizar audio.');
  const buffer=await decodeAudioSample(file);
  if(activeOneShotSamplePreview){
    try{activeOneShotSamplePreview.stop();}
    catch{
      // La muestra anterior puede haber terminado por sí sola.
    }
    activeOneShotSamplePreview=null;
  }
  const source=audio.createBufferSource();
  const gain=audio.createGain();
  source.buffer=buffer;
  source.playbackRate.value=number(design[`${prefix}SampleRate`],1,.5,2);
  gain.gain.value=number(design[`${prefix}SampleVolume`],.5,.02,1);
  source.connect(gain);
  gain.connect(audio.destination);
  source.addEventListener('ended',()=>{
    if(activeOneShotSamplePreview===source)activeOneShotSamplePreview=null;
    source.disconnect();
    gain.disconnect();
  },{once:true});
  activeOneShotSamplePreview=source;
  source.start();
}

export async function previewSustainedSample(file,design={}){
  const audio=ensureAudio();
  if(!audio)throw new Error('Este navegador no permite previsualizar audio.');
  const decodedBuffer=await decodeAudioSample(file);
  const buffer=createSeamlessAudioLoop(audio,decodedBuffer,design);
  if(activeOneShotSamplePreview){
    try{activeOneShotSamplePreview.stop();}
    catch{
      // La muestra anterior puede haber terminado por sí sola.
    }
    activeOneShotSamplePreview=null;
  }
  const now=audio.currentTime;
  const duration=1.5;
  const source=audio.createBufferSource();
  const gain=audio.createGain();
  source.buffer=buffer;
  source.loop=true;
  source.playbackRate.value=number(design.soundSampleRate,1,.5,2);
  const volume=number(design.soundSampleVolume,.5,.02,1);
  gain.gain.setValueAtTime(.001,now);
  gain.gain.exponentialRampToValueAtTime(volume,now+.08);
  gain.gain.setValueAtTime(volume,now+duration-.16);
  gain.gain.exponentialRampToValueAtTime(.001,now+duration);
  source.connect(gain);
  gain.connect(audio.destination);
  source.addEventListener('ended',()=>{
    if(activeOneShotSamplePreview===source)activeOneShotSamplePreview=null;
    source.disconnect();
    gain.disconnect();
  },{once:true});
  activeOneShotSamplePreview=source;
  source.start(now);
  source.stop(now+duration+.03);
}

function noise(duration,volume,frequency,filterType='bandpass'){
  const audio=ensureAudio();
  if(!audio||volume<=0)return;
  const now=audio.currentTime;
  const source=audio.createBufferSource();
  const filter=audio.createBiquadFilter();
  const gain=audio.createGain();
  source.buffer=noiseBuffer;
  filter.type=FILTERS.has(filterType)?filterType:'bandpass';
  filter.frequency.setValueAtTime(frequency,now);
  filter.frequency.exponentialRampToValueAtTime(Math.max(70,frequency*.45),now+duration);
  gain.gain.setValueAtTime(Math.max(.001,volume),now);
  gain.gain.exponentialRampToValueAtTime(.001,now+duration);
  source.connect(filter);filter.connect(gain);gain.connect(audio.destination);
  source.start(now);source.stop(now+duration+.02);
}

export function previewOneShot(design={},prefix='sound'){
  const audio=ensureAudio();
  if(!audio)return;
  const now=audio.currentTime;
  const wave=WAVES.has(design[`${prefix}Wave`])?design[`${prefix}Wave`]:'square';
  const tone=number(design[`${prefix}Tone`],320,30,4800);
  const duration=number(design[`${prefix}Duration`],.12,.03,2.5);
  const filterValue=number(design[`${prefix}Filter`],1800,70,12000);
  const noiseGain=number(design[`${prefix}NoiseGain`],.05,0,.5);
  const toneGain=number(design[`${prefix}ToneGain`],.07,0,.5);
  const oscillator=audio.createOscillator();
  const filter=audio.createBiquadFilter();
  const gain=audio.createGain();
  oscillator.type=wave;
  oscillator.frequency.setValueAtTime(tone,now);
  oscillator.frequency.exponentialRampToValueAtTime(Math.max(28,tone*.52),now+duration);
  filter.type=filterValue>2200?'highpass':'bandpass';
  filter.frequency.value=filterValue;
  gain.gain.setValueAtTime(Math.max(.001,toneGain),now);
  gain.gain.exponentialRampToValueAtTime(.001,now+duration);
  oscillator.connect(filter);filter.connect(gain);gain.connect(audio.destination);
  oscillator.start(now);oscillator.stop(now+duration+.02);
  noise(duration,noiseGain,filterValue,filter.type);
}

export function previewEngine(design={}){
  const audio=ensureAudio();
  if(!audio)return;
  stopEngineSamplePreview();
  stopActiveEngineSampleLoop();
  const now=audio.currentTime;
  const duration=2.2;
  const oscillator=audio.createOscillator();
  const filter=audio.createBiquadFilter();
  const gain=audio.createGain();
  const wave=WAVES.has(design.wave)?design.wave:'sawtooth';
  const base=number(design.baseTone,58,25,280);
  const speedPitch=number(design.speedPitch,7,1,18);
  const boostPitch=number(design.boostPitch,45,0,160);
  oscillator.type=wave;
  oscillator.frequency.setValueAtTime(base,now);
  oscillator.frequency.exponentialRampToValueAtTime(base+speedPitch*14,now+1.35);
  oscillator.frequency.exponentialRampToValueAtTime(base+speedPitch*17+boostPitch,now+1.72);
  oscillator.frequency.exponentialRampToValueAtTime(base*1.12,now+duration);
  filter.type=FILTERS.has(design.filterType)?design.filterType:'lowpass';
  filter.frequency.value=number(design.filter,620,70,12000);
  filter.Q.value=number(design.resonance,1,0,18);
  gain.gain.setValueAtTime(.001,now);
  gain.gain.exponentialRampToValueAtTime(number(design.volume,.035,.002,.18),now+.08);
  gain.gain.setValueAtTime(number(design.volume,.035,.002,.18),now+duration-.12);
  gain.gain.exponentialRampToValueAtTime(.001,now+duration);
  oscillator.connect(filter);filter.connect(gain);gain.connect(audio.destination);
  oscillator.start(now);oscillator.stop(now+duration+.03);
  noise(duration,number(design.noiseGain,.005,0,.08),number(design.filter,620,70,12000),filter.type);
}
